import type { AllowedAttribute, DisallowedTagsModes } from 'sanitize-html';
import sanitizeHtml from 'sanitize-html';

// Pulled from DOMPurify's list of allowed attributes to be used for backwards compatibility.
const defaultAttributes: AllowedAttribute[] = [
  'accept',
  'action',
  'align',
  'alt',
  'autocapitalize',
  'autocomplete',
  'autopictureinpicture',
  'autoplay',
  'background',
  'bgcolor',
  'border',
  'capture',
  'cellpadding',
  'cellspacing',
  'checked',
  'cite',
  'class',
  'clear',
  'color',
  'cols',
  'colspan',
  'controls',
  'controlslist',
  'coords',
  'crossorigin',
  'datetime',
  'decoding',
  'default',
  'dir',
  'disabled',
  'disablepictureinpicture',
  'disableremoteplayback',
  'download',
  'draggable',
  'enctype',
  'enterkeyhint',
  'face',
  'for',
  'headers',
  'height',
  'hidden',
  'high',
  'href',
  'hreflang',
  'id',
  'inputmode',
  'integrity',
  'ismap',
  'kind',
  'label',
  'lang',
  'list',
  'loading',
  'loop',
  'low',
  'max',
  'maxlength',
  'media',
  'method',
  'min',
  'minlength',
  'multiple',
  'muted',
  'name',
  'nonce',
  'noshade',
  'novalidate',
  'nowrap',
  'open',
  'optimum',
  'pattern',
  'placeholder',
  'playsinline',
  'poster',
  'preload',
  'pubdate',
  'radiogroup',
  'readonly',
  'rel',
  'required',
  'rev',
  'reversed',
  'role',
  'rows',
  'rowspan',
  'scope',
  'selected',
  'shape',
  'size',
  'sizes',
  'slot',
  'span',
  'spellcheck',
  'src',
  'srclang',
  'srcset',
  'start',
  'step',
  'style',
  'summary',
  'tabindex',
  'title',
  'translate',
  'type',
  'usemap',
  'valign',
  'value',
  'width',
  'xmlns',
];

const allowedAttributes = (allowSafeTags: boolean, allowedDangerousTags: string[]) => {
  const attributes: Record<string, AllowedAttribute[]> = { ...sanitizeHtml.defaults.allowedAttributes };

  // Additional iframe specific attributes we've historically supported
  if (allowedDangerousTags.includes('iframe')) {
    attributes.iframe = ['allow', 'allowfullscreen', 'frameborder', 'scrolling'];
  }

  // Allow all the default attributes from DOMPurify for backwards compatibility.
  allowedTags(allowSafeTags, allowedDangerousTags).forEach((tag) => {
    attributes[tag] ||= [];
    attributes[tag] = attributes[tag].concat(defaultAttributes);
  });

  return attributes;
};

const allowedTags = (allowSafeTags: boolean, allowedDangerousTags: string[]): string[] => {
  const tags = allowSafeTags ? ['del', 'img', ...sanitizeHtml.defaults.allowedTags] : [];
  return tags.concat(allowedDangerousTags);
};

// sanitizeHtml requires an allowlist of permitted hosts for iframes. Since we can't
// build an allowlist ahead of time, we'll dynamically find and allow any host in the
// string. This should be safe for any options passed into the sanitize function since
// iframes are stripped unless they're explicitly permitted. In cases where we permit
// iframes, we permit any host.
const allowedIframeHostnames = (text: string) => {
  const urlRegex =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g;
  let match: RegExpExecArray;
  const hostnames: string[] = [];
  // eslint-disable-next-line no-cond-assign
  while ((match = urlRegex.exec(text)) !== null) {
    try {
      const url = new URL(match[0]);
      hostnames.push(url.hostname);
    } catch (e) {
      // Ignore invalid URLs
    }
  }
  return hostnames;
};

// destroyRelativeProtocols adds protocols to all `src` attributes. We had an issue
// where customers were doing iframes with `src="//www.youtube.com/embed/B0LfnaGmLTQ"`
// and it would strip the src entirely. So we want to find those and add `https:`.
// Since we only server onver HTTPS, this is safe.
const destroyRelativeProtocols = (text: string, allowedDangerousTags: string[]): string => {
  if (!allowedDangerousTags.includes('iframe')) return text;

  const parser = new DOMParser();
  // Parse the text into a document
  const doc = parser.parseFromString(text, 'text/html');

  // Find all iframe elements
  const iframes = doc.querySelectorAll('iframe');

  // Iterate over the iframe elements and update their src attributes if necessary
  iframes.forEach((iframe) => {
    const src = iframe.getAttribute('src');
    // Check if src has relative protocol
    if (src && src.startsWith('//')) {
      // Update the src attribute by adding the https protocol
      iframe.setAttribute('src', `https:${src}`);
    }
  });

  // Serialize the document back to a string
  return new XMLSerializer().serializeToString(doc);
};

/**
 * Sanitizes user input for display to prevent XSS and HTML injection attacks.
 *
 * @param text - The user input to sanitize.
 * @param options - An object containing configuration for the sanitization process.
 *   - options.allowSafeTags: A boolean indicating whether to allow basic HTML tags that are considered safe.
 *   - options.allowedDangerousTags: An array of strings representing additional HTML tags that are considered dangerous, which will be allows to pass through sanitization.
 *
 * @returns The sanitized user input. Will be match the text parameter, but disallowed HTML will be stripped out.
 *
 * @example
 *  // When allowSafeTags is false, all HTML tags are stripped.
 * sanitizeUserInput('hi') // Returns: `hi`
 * sanitizeUserInput('<h1>hi</h1>') // Returns: `hi`
 * sanitizeUserInput('<iframe src="http://dangernoodle/web.html"></iframe>') // Returns: ``
 *
 * // When allowSafeTags is true, basic HTML tags are permitted to pass through. This will impact the middle example above:
 * sanitizeUserInput('<h1>hi</h1>', { allowSafeTags: true }) // Returns `<h1>hi</h1>` since <h1> tags don't present a security issue.
 *
 * // When you need an unsafe tag, you'll need to explicitly list it in the `allowedDangerousTags` like this:
 * sanitizeUserInput('<iframe src="http://dangernoodle/web.html"></iframe>', { allowedDangerousTags: 'iframe' }) // Returns: `<iframe src="http://dangernoodle/web.html"></iframe>`
 */

export interface SanitizeUserInputOptions {
  allowSafeTags?: boolean;
  allowedDangerousTags?: string[];
  disallowedTagsMode?: DisallowedTagsModes;
}

// define function overloads to help TS understand that string=>string and string[]=>string[]
export function sanitizeUserInput(text: string | null | undefined, options?: SanitizeUserInputOptions): string;
export function sanitizeUserInput(text: string[] | null | undefined, options?: SanitizeUserInputOptions): string[];
export function sanitizeUserInput(
  text: string | string[] | null | undefined,
  { allowSafeTags = false, allowedDangerousTags = [], disallowedTagsMode = 'discard' }: SanitizeUserInputOptions = {},
): string | string[] {
  if (!text) {
    return '';
  }

  if (typeof text === 'string') {
    const tags = allowedTags(allowSafeTags, allowedDangerousTags);
    const protocolledText = destroyRelativeProtocols(text, tags);
    return sanitizeHtml(protocolledText, {
      allowedTags: tags,
      allowedAttributes: allowedAttributes(allowSafeTags, allowedDangerousTags),
      allowedIframeHostnames: allowedIframeHostnames(protocolledText),
      allowIframeRelativeUrls: true,
      disallowedTagsMode,
    });
  }
  if (Array.isArray(text)) {
    return (text as string[]).map((t: string) => sanitizeUserInput(t, { allowSafeTags, allowedDangerousTags }));
  }
  throw new Error('invalid argument type');
}
